<template>
  <BaseCard
    gray
    :heading="$t('employee.profile.languages')"
  >
    <BaseText v-if="!selectedLanguages.length">
      {{ $t('employee.profile.no_languages') }}
    </BaseText>
    <Flex
      v-else
      direction="column"
      :gap=".5"
    >
      <div
        v-for="(language, index) in selectedLanguages"
        :key="index"
        :class="$style.language"
      >
        <BaseDropdown
          v-model="language.code"
          :options="languageOptions"
          searchable
          required
        />
        <BaseDropdown
          v-model="language.proficiency"
          :options="proficiencyOptions"
        />
        <BaseIcon
          name="close-circle"
          @click="removeLanguage(index)"
        />
      </div>
    </Flex>
    <BaseText
      :mt=".5"
      @click="addLanguage"
    >
      + {{ $t('employee.profile.add_language') }}
    </BaseText>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { useI18n } from 'vue-i18n';
import { useCompanyStore } from '@/stores/company';
import { useLocalizationStore } from '@/stores/localization';
import type { LanguageEntryAttributes } from '@/types';
import { LanguageProficiency } from '@/types';
import { getLanguageList } from './language-list.js';

const props = defineProps<{
  modelValue: LanguageEntryAttributes[];
}>();

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const localizationStore = useLocalizationStore();

const emitValue = (value: LanguageEntryAttributes[]) => {
  emit('update:modelValue', value);
};

const selectedLanguages = computed({
  get: () => props.modelValue,
  set: (value: LanguageEntryAttributes[]) => emitValue(value),
});

const languageOptions = computed(() =>
  getLanguageList(localizationStore.language).map((language) => ({
    label: language.name,
    value: language.code,
  })),
);

const proficiencyOptions = computed(() => [
  {
    label: t('employee.profile.proficiency.beginner'),
    value: LanguageProficiency.Beginner,
  },
  {
    label: t('employee.profile.proficiency.intermediate'),
    value: LanguageProficiency.Intermediate,
  },
  {
    label: t('employee.profile.proficiency.advanced'),
    value: LanguageProficiency.Advanced,
  },
  {
    label: t('employee.profile.proficiency.native'),
    value: LanguageProficiency.Native,
  },
]);

const addLanguage = () => {
  const companyLanguage = useCompanyStore().company.language.toLowerCase();
  const companyLanguageAlreadySelected = selectedLanguages.value.find(
    (language) => language.code === companyLanguage,
  );

  emitValue([
    ...selectedLanguages.value,
    {
      code: companyLanguageAlreadySelected ? 'en' : companyLanguage,
      proficiency: LanguageProficiency.Native,
    },
  ]);
};

const removeLanguage = (index: number) => {
  emitValue(selectedLanguages.value.filter((_, i) => i !== index));
};
</script>

<style
  lang="scss"
  module
>
.language {
  display: flex;
  align-items: center;
  gap: $spacing * .5;
}
</style>
